import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import dayjs from "dayjs";
import { Menu, MenuItem } from "@mui/material";
import { surfaceOf, createChart, type Chart, type ChartOptions, type CrosshairEvent, type DeepPartial, type LineStyle, type SeriesModel } from "@ortex-charts/core";
import { addEvents, type ChartEvent } from "@ortex-charts/financial";
import type { AlignMode, Bar } from "@ortex-charts/math";
import { ensureOrtexCharts } from "@/lib/ortex-charts/setup";
import { useOrtexChartTheme } from "@/lib/ortex-charts/theme";
import { alpha } from "@/lib/ortex-charts/color";
import { exportChartPNG, exportRowsCSV } from "@/lib/ortex-charts/export";
import { formatDate, nFormatter } from "@/lib/utils";
import { useZoneFor } from "@/lib/time/state";
import { zoneLabel } from "@/lib/time/zone";
import styles from "./TimeSeriesChart.module.scss";

/**
 * The general time-series chart of the app: any number of lines, areas,
 * columns or candles over one timeline, on the right scale, the left scale or
 * a scale of their own, in the main pane or a pane below, with event markers,
 * horizontal reference lines, a shared tooltip, range buttons and export.
 *
 * Every chart that used to be a `line`/`column`/`area` options object for
 * the previous chart engine is this component with a `series` array. Data is `[time, value]`
 * pairs in epoch milliseconds, as the platform returns them, or bars.
 */
export type TimeSeriesType = "line" | "spline" | "area" | "column" | "candlestick" | "baseline" | "step";

export interface TimeSeries {
    id: string;
    title: string;
    type?: TimeSeriesType;
    data: ReadonlyArray<readonly [number, number]> | ReadonlyArray<{ time: number; value: number }> | Bar[];
    color?: string;
    /**
     * How this series is placed on the chart timeline. The library default, `forwardFill`,
     * carries the last value across every later master time, which is right for a daily
     * series over intraday bars and wrong for an overlay that ends before the timeline does
     * (it extrapolates into the future). Use `exact` when the series shares the timeline's
     * grid and must not extend past its last point.
     */
    align?: AlignMode;
    /** `right` (default), `left`, or any other id for a scale of its own that is not drawn. */
    axis?: string;
    /** Series with the same pane id share a pane below the main one. */
    pane?: string;
    lineWidth?: number;
    lineStyle?: LineStyle;
    visible?: boolean;
    /** Digits after the decimal point in labels and the tooltip. */
    precision?: number;
    /** `compact` writes 1.2M, `percent` appends %, `price` is plain. */
    format?: "price" | "compact" | "percent";
    /** Overrides the format in the tooltip. */
    formatter?: (value: number) => string;
    /** Columns: colour by sign around zero. */
    colorBySign?: boolean;
    /** Areas: fill opacity. */
    fillOpacity?: number;
    /** Baseline series: the value the fill flips around. */
    baseValue?: number;
    /** Draw the last value on the scale. */
    lastValue?: boolean;
    legend?: boolean;
    /** `false` keeps the series out of the tooltip and its nearest-line pick (a band drawn behind a line). */
    tooltip?: boolean;
    zIndex?: number;
    /** Candles: colours for up and down bodies. */
    upColor?: string;
    downColor?: string;
}

export interface TimeSeriesLine {
    value: number;
    axis?: string;
    color?: string;
    title?: string;
    lineStyle?: LineStyle;
    lineWidth?: number;
}

export interface TimeSeriesAxis {
    id: string;
    /** Fraction of the pane kept empty above and below. */
    margins?: { top: number; bottom: number };
    visible?: boolean;
    invert?: boolean;
    mode?: "linear" | "log" | "percent";
}

export interface RangeButton {
    label: string;
    /** Days back from the last bar; 0 is everything. */
    days: number;
    /** `ytd` starts at 1 January of the last bar's year. */
    kind?: "days" | "ytd";
}

const EVENT_PALETTE = ["#31ABA6", "#F5A623", "#7B61FF", "#E0455E", "#3B9BFF", "#9BC53D", "#FF7AB6", "#B8B8B8"];

export const DEFAULT_RANGE_BUTTONS: RangeButton[] = [
    { label: "1m", days: 31 },
    { label: "6m", days: 183 },
    { label: "YTD", days: 0, kind: "ytd" },
    { label: "1y", days: 366 },
    { label: "3y", days: 1096 },
    { label: "All", days: 0 },
];

export interface TimeSeriesChartProps {
    series: TimeSeries[];
    /** Reference lines across a scale. */
    lines?: TimeSeriesLine[];
    /** Scale options, keyed by axis id. */
    axes?: TimeSeriesAxis[];
    /** Pane heights below the main pane, as a ratio of the main pane. */
    paneHeights?: Record<string, number>;
    /** Events drawn on the timeline (dividends, earnings, ...). */
    events?: ChartEvent[];
    /** A strip of the event groups (from `event.group`) as toggles, coloured like their markers. */
    eventLegend?: boolean;
    /**
     * The value beside the cursor for the hovered line. Defaults to whatever
     * `crosshair` is: a chart drawn without a crosshair is a picture, not
     * something being read at a point, so it gets no readout either.
     */
    hoverReadout?: boolean;
    /** What that readout says; see the library's template placeholders. */
    hoverTemplate?: string;
    /** Range buttons above the chart; `false` hides them. */
    rangeButtons?: RangeButton[] | false;
    /** Initial range, as one of the buttons' labels or days back. */
    initialRange?: string | number;
    /** Show the legend with values at the crosshair. Default true when there is more than one series. */
    legend?: boolean;
    /** The time axis. */
    timeAxis?: boolean;
    /** Grid lines. */
    grid?: boolean;
    /** Crosshair. Default true. */
    crosshair?: boolean;
    /** Tooltip beside the pointer with every series' value. Default true. */
    tooltip?: boolean;
    /** Custom tooltip title for a time; defaults to the formatted date. */
    tooltipTitle?: (time: number) => string;
    /** Extra tooltip rows for a time (below the series rows). */
    tooltipExtra?: (time: number) => Array<{ label: string; value: string; color?: string }>;
    /** `1D` (default), `1W`, `1M`, `60`, `1`... how the timeline is spaced and labelled. */
    resolution?: string;
    /**
     * An explicit zone for the time axis and the default tooltip title. Usually leave it out: daily
     * resolutions (`1D`/`1W`/`1M`) are calendar dates and stay UTC; intraday (numeric) resolutions
     * follow `timeRole` (#1281).
     */
    timeZone?: string;
    /** What an intraday axis shows: "market" (default, Settings S2) or "trading" (S3). */
    timeRole?: "market" | "trading";
    /** The instrument's `exchange_tz`, which "market" and "trading" default to. */
    exchangeTz?: string | null;
    /** Fit every bar in view after data changes. Default true. */
    fit?: boolean;
    /**
     * Widest a bar may be drawn, in pixels. Default 60, the library's.
     *
     * `fit` divides the width by the bar count and clamps to this, and the
     * leftover width is padded with empty bars *before* the first point. For a
     * daily price series the cap never binds; for a dozen monthly readings it
     * binds hard, and the chart spent three quarters of its width on phantom
     * days before the data. A sparse series passes a large value here so its
     * few points can spread across the whole chart.
     */
    maxBarSpacing?: number;
    /** Let the user scroll and zoom. Default true. */
    interactive?: boolean;
    /** Export menu (PNG, CSV). Default true when range buttons are shown. */
    exportMenu?: boolean;
    exportName?: string;
    height?: number | string;
    className?: string;
    style?: CSSProperties;
    /** Rendered above the chart on the right, beside the range buttons. */
    toolbar?: ReactNode;
    /** Replace the canvas with this while there is no data. */
    empty?: ReactNode;
    /** Blur the chart and disable pointer events (obfuscated data). */
    obfuscated?: boolean;
    onClick?: (e: { time: number | null; values: Record<string, number>; event: CrosshairEvent }) => void;
    onCrosshair?: (e: { time: number | null; values: Record<string, number> }) => void;
    onReady?: (chart: Chart) => void;
    /**
     * Series ids hidden from the chart, when the caller owns that state — an
     * external menu of what is on and off the chart has to agree with the
     * legend, and it can only do that if one of them holds the set. Left
     * undefined, the chart keeps the set itself.
     */
    hiddenIds?: ReadonlySet<string>;
    /** Told whenever a legend click, a line click or the legend menu changes what is hidden. */
    onHiddenChange?: (hidden: Set<string>) => void;
    /**
     * Clicking a legend row — its colour square or its title — hides that
     * series; shift-click shows only it. Default true: the caret menu was the
     * only way to hide a line, and nobody found it.
     */
    legendToggles?: boolean;
    /** Clicking a line on the canvas hides it. Off by default; on for charts whose lines are a pick. */
    clickToHide?: boolean;
}

/** How close, in pixels, a click has to land to a line to count as clicking it. */
export const LINE_HIT_PX = 14;

/**
 * The colour each series is drawn in, by id, using the chart's own rule: the
 * series' own colour, else the palette entry at its position in the list.
 *
 * Exported so a menu beside the chart can colour its chips the way the lines
 * are drawn. Position counts hidden series too, so hiding one line never
 * recolours the rest.
 */
export const seriesColors = (series: ReadonlyArray<Pick<TimeSeries, "id" | "color">>, palette: ReadonlyArray<string>): Map<string, string> => new Map(series.map((s, i) => [s.id, s.color ?? palette[i % palette.length] ?? ""]));

/**
 * The id of the line nearest a click, or null when nothing is within reach.
 *
 * Pure and exported: `distances` is each candidate's vertical distance from
 * the pointer, already measured on its own pane.
 */
export const nearestWithin = (distances: ReadonlyArray<{ id: string; distance: number }>, within: number = LINE_HIT_PX): string | null => {
    let best: { id: string; distance: number } | null = null;
    for (const d of distances) if (Number.isFinite(d.distance) && d.distance <= within && (!best || d.distance < best.distance)) best = d;
    return best?.id ?? null;
};

export interface TimeSeriesChartHandle {
    chart: Chart | null;
    container: HTMLDivElement | null;
    setRange(fromTime: number, toTime: number): void;
    fit(): void;
    exportPNG(name?: string): Promise<void>;
    exportCSV(name?: string): Promise<void>;
}

const CORE_TYPE: Record<TimeSeriesType, string> = { line: "line", spline: "line", area: "area", column: "histogram", candlestick: "candlestick", baseline: "baseline", step: "line" };

/** Ascending by time with one point per time, as the chart requires; the platform sometimes answers newest first. */
function ordered<T extends { time: number }>(points: T[]): T[] {
    for (let i = 1; i < points.length; i++)
        if (points[i].time <= points[i - 1].time)
            return Array.from(
                new Map(
                    points
                        .slice()
                        .sort((a, b) => a.time - b.time)
                        .map((p) => [p.time, p])
                ).values()
            );
    return points;
}

function toPoints(data: TimeSeries["data"]): Array<{ time: number; value: number }> | Bar[] {
    if (!data || !data.length) return [];
    const first = data[0] as unknown;
    if (Array.isArray(first)) return ordered((data as ReadonlyArray<readonly [number, number]>).filter((p) => p && p[0] != null && p[1] != null && Number.isFinite(p[1])).map(([time, value]) => ({ time, value })));
    if (typeof first === "object" && first && "open" in (first as object)) return ordered((data as Bar[]).slice());
    return ordered((data as ReadonlyArray<{ time: number; value: number }>).filter((p) => p && p.time != null && Number.isFinite(p.value)));
}

function valueFormatter(s: TimeSeries): (v: number) => string {
    if (s.formatter) return s.formatter;
    const p = s.precision ?? 2;
    if (s.format === "compact") return (v) => nFormatter(v, p);
    if (s.format === "percent") return (v) => `${v.toFixed(p)}%`;
    return (v) => v.toLocaleString(undefined, { minimumFractionDigits: p, maximumFractionDigits: p });
}

/** CSV rows, one per time. Daily points are dates; intraday ones keep the time, as ISO UTC (#1281). */
export function seriesCSVRows(series: TimeSeries[], intraday = false): Record<string, unknown>[] {
    const byTime = new Map<number, Record<string, unknown>>();
    for (const s of series) {
        for (const p of toPoints(s.data)) {
            const row = byTime.get(p.time) ?? { date: intraday ? dayjs.utc(p.time).format("YYYY-MM-DD[T]HH:mm:ss[Z]") : dayjs.utc(p.time).format("YYYY-MM-DD") };
            if ("open" in p) {
                row[`${s.title} open`] = p.open;
                row[`${s.title} high`] = p.high;
                row[`${s.title} low`] = p.low;
                row[`${s.title} close`] = p.close;
                row[`${s.title} volume`] = p.volume;
            } else row[s.title] = p.value;
            byTime.set(p.time, row);
        }
    }
    return Array.from(byTime.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, row]) => row);
}

const TimeSeriesChart = forwardRef<TimeSeriesChartHandle, TimeSeriesChartProps>(function TimeSeriesChart(props, ref) {
    const {
        series,
        lines,
        axes,
        paneHeights,
        events,
        eventLegend = false,
        hoverReadout,
        hoverTemplate,
        rangeButtons = false,
        initialRange,
        legend,
        timeAxis = true,
        grid = true,
        crosshair = true,
        tooltip = true,
        tooltipTitle,
        tooltipExtra,
        resolution = "1D",
        timeZone: timeZoneProp,
        timeRole = "market",
        exchangeTz,
        fit = true,
        maxBarSpacing = 60,
        interactive = true,
        exportMenu,
        exportName = "chart",
        height,
        className,
        style,
        toolbar,
        empty,
        obfuscated,
        onClick,
        onCrosshair,
        onReady,
        hiddenIds,
        onHiddenChange,
        legendToggles = true,
        clickToHide = false,
    } = props;
    const theme = useOrtexChartTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<Chart | null>(null);
    const seriesRef = useRef<Map<string, SeriesModel>>(new Map());
    const eventsRef = useRef<ReturnType<typeof addEvents> | null>(null);
    const [chart, setChart] = useState<Chart | null>(null);
    const [activeRange, setActiveRange] = useState<string | null>(typeof initialRange === "string" ? initialRange : null);
    const [tip, setTip] = useState<{ x: number; y: number; title: string; rows: Array<{ label: string; value: string; color?: string; nearest?: boolean }> } | null>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    /**
     * Series the reader has hidden from the legend. The engine's legend has no
     * click-to-hide of its own; its caret raises a menu event that nothing in
     * the app answered, which is why "there is no way to disable things on the
     * legend". The menu below answers it, and this set is what it edits.
     */
    const [ownHidden, setOwnHidden] = useState<Set<string>>(() => new Set());
    // Controlled when the caller passes the set (an external menu shares it),
    // otherwise the chart's own. Either way every change goes out through
    // `onHiddenChange`, so a caller can mirror without owning.
    const hidden: ReadonlySet<string> = hiddenIds ?? ownHidden;
    const hiddenRef = useRef(hidden);
    hiddenRef.current = hidden;
    const [legendMenu, setLegendMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null);
    // Daily points are calendar dates at midnight UTC and stay UTC; an intraday axis shows real
    // instants in the zone its role resolves to (exchange by default), and so does its tooltip.
    const intraday = /^\d+$/.test(resolution);
    const roleZone = useZoneFor(timeRole, exchangeTz);
    const timeZone = timeZoneProp ?? (intraday ? roleZone : "UTC");
    const latest = useRef({ ...props, zone: timeZone });
    latest.current = { ...props, zone: timeZone };
    const setHidden = useCallback((next: Set<string>) => {
        if (latest.current.hiddenIds === undefined) setOwnHidden(next);
        latest.current.onHiddenChange?.(next);
    }, []);
    /** Whether the titled box is this chart's readout; see `cardSized` below. */
    const boxRef = useRef(true);

    const hasData = series.some((s) => s.visible !== false && s.data && s.data.length > 0);
    const showLegend = legend ?? series.filter((s) => s.visible !== false && s.legend !== false).length > 1;

    /**
     * A card-sized chart -- a table cell, a stamp, a sparkline beside a number --
     * is smaller than the titled tooltip box that describes it, so it reads the
     * compact value beside the cursor instead. Anything that cannot be panned is
     * one of those, as is anything under 180 pixels tall.
     */
    const cardSized = interactive === false || (typeof height === "number" && height < 180);
    /** The box, on a chart with room for it. */
    const boxTooltip = tooltip && !cardSized;
    /** The value beside the cursor, when the box is not carrying it. */
    const readout = hoverReadout ?? (crosshair && !boxTooltip);
    boxRef.current = boxTooltip;

    const options = useMemo<DeepPartial<ChartOptions>>(
        () => ({
            theme,
            timeZone,
            resolution,
            autoSize: true,
            rightPriceScale: { visible: true, borderVisible: false, scaleMargins: { top: 0.1, bottom: 0.1 } },
            leftPriceScale: { visible: series.some((s) => s.axis === "left" && s.visible !== false), borderVisible: false, scaleMargins: { top: 0.1, bottom: 0.1 } },
            timeScale: { visible: timeAxis, borderVisible: false, fixLeftEdge: true, fixRightEdge: true, rightOffsetPx: 4, maxBarSpacing },
            grid: { vertical: false, horizontal: grid },
            crosshair: { mode: crosshair ? "magnet" : "hidden", vertLine: { labelVisible: timeAxis }, horzLine: { labelVisible: crosshair } },
            legend: { visible: showLegend, showValues: true },
            // The value beside the cursor for the line under it, and never
            // alongside the titled box below: two readouts of the same number is
            // what a card looked like before this chose between them. A card
            // takes the small one, a full chart the box, and either can be
            // asked for explicitly.
            tooltip: readout ? { mode: "hovered" as const, template: hoverTemplate ?? (cardSized ? "{name} {value}" : "{name}  {value}\n1d {value % value[-1]}   1w {value % value[-7]}") } : { mode: "none" as const },
            handleScroll: interactive,
            handleScale: interactive,
            kineticScroll: interactive,
            branding: { visible: false },
        }),
        [theme, timeZone, resolution, timeAxis, grid, crosshair, showLegend, interactive, series, readout, cardSized, hoverTemplate, maxBarSpacing]
    );

    // Create once.
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        let disposed = false;
        void ensureOrtexCharts().then(() => {
            if (disposed || !containerRef.current) return;
            const c = createChart(containerRef.current, options);
            chartRef.current = c;
            setChart(c);
            latest.current.onReady?.(c);
        });
        return () => {
            disposed = true;
            chartRef.current?.remove();
            chartRef.current = null;
            seriesRef.current.clear();
            eventsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Options follow the theme and props.
    useEffect(() => {
        chart?.applyOptions(options);
    }, [chart, options]);

    // Axes.
    useEffect(() => {
        if (!chart || !axes) return;
        for (const a of axes) {
            const mode = a.mode === "log" ? "logarithmic" : a.mode === "percent" ? "percentage" : a.mode === "linear" ? "normal" : undefined;
            chart.priceScale(a.id).applyOptions({ scaleMargins: a.margins, visible: a.visible, invert: a.invert, mode });
        }
    }, [chart, axes]);

    // Series: reconcile by id.
    useEffect(() => {
        if (!chart) return;
        const seen = new Set<string>();
        const paneIds = new Map<string, string>();
        for (const s of series) {
            if (s.pane && !paneIds.has(s.pane)) {
                const existing = chart.panesList().find((p) => p.id === s.pane);
                if (!existing) chart.addPane({ id: s.pane, heightRatio: paneHeights?.[s.pane] ?? 0.3 });
                paneIds.set(s.pane, s.pane);
            }
        }
        series.forEach((s, i) => {
            seen.add(s.id);
            const type = CORE_TYPE[s.type ?? "line"];
            const color = s.color ?? theme.palette[i % theme.palette.length];
            const fmt = valueFormatter(s);
            const base = {
                title: s.title,
                color,
                lineWidth: s.lineWidth ?? 2,
                lineStyle: s.lineStyle ?? "solid",
                priceScaleId: s.axis ?? "right",
                pane: s.pane,
                align: s.align ?? "forwardFill",
                visible: s.visible !== false && !hidden.has(s.id),
                lastValueVisible: s.lastValue ?? false,
                priceLineVisible: false,
                legendVisible: s.legend !== false,
                zIndex: s.zIndex ?? (type === "histogram" ? 0 : 1),
                priceFormat: { type: "custom" as const, precision: s.precision ?? 2, minMove: 1 / 10 ** (s.precision ?? 2), formatter: fmt },
                crosshairMarkerVisible: type !== "histogram",
            };
            const extra: Record<string, unknown> =
                type === "line"
                    ? { curve: s.type === "spline" ? "monotone" : s.type === "step" ? "step" : "linear" }
                    : type === "area"
                      ? { curve: s.type === "spline" ? "monotone" : "linear", topColor: alpha(color, s.fillOpacity ?? 0.35), bottomColor: alpha(color, 0.02) }
                      : type === "histogram"
                        ? { colorMode: s.colorBySign ? "sign" : "fixed", upColor: theme.up, downColor: theme.down, widthRatio: 0.7 }
                        : type === "candlestick"
                          ? { upColor: s.upColor ?? theme.up, downColor: s.downColor ?? theme.down, wickUpColor: s.upColor ?? theme.up, wickDownColor: s.downColor ?? theme.down }
                          : type === "baseline"
                            ? { baseValue: s.baseValue ?? 0, topLineColor: theme.up, bottomLineColor: theme.down, topFillColor: alpha(theme.up, 0.25), bottomFillColor: alpha(theme.down, 0.25) }
                            : {};
            let model = seriesRef.current.get(s.id);
            if (model && model.type !== type) {
                chart.removeSeries(model.id);
                model = undefined;
            }
            if (!model) {
                model = chart.addSeries(type, { ...base, ...extra });
                seriesRef.current.set(s.id, model);
            } else model.applyOptions({ ...base, ...extra });
            model.setData(toPoints(s.data));
        });
        for (const [id, model] of Array.from(seriesRef.current.entries())) {
            if (!seen.has(id)) {
                chart.removeSeries(model.id);
                seriesRef.current.delete(id);
            }
        }
        if (fit && !activeRange) chart.timeScale.fitContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chart, series, theme, paneHeights, hidden]);

    // Reference lines.
    useEffect(() => {
        if (!chart) return;
        const ids: Array<[string, string]> = [];
        for (const l of lines ?? []) {
            const owner = series.find((s) => (s.axis ?? "right") === (l.axis ?? "right") && s.visible !== false) ?? series[0];
            const model = owner && seriesRef.current.get(owner.id);
            if (!model) continue;
            ids.push([model.id, chart.addPriceLine(model.id, { price: l.value, color: l.color ?? theme.textMuted, title: l.title ?? "", lineStyle: l.lineStyle ?? "dashed", lineWidth: l.lineWidth ?? 1, axisLabelVisible: true })]);
        }
        return () => {
            for (const [sid, lid] of ids) {
                try {
                    chart.removePriceLine(sid, lid);
                } catch {
                    // series already gone
                }
            }
        };
    }, [chart, lines, series, theme]);

    // Events on the main series.
    const [hiddenGroups, setHiddenGroups] = useState<ReadonlySet<string>>(() => new Set());
    /** Event groups in first-seen order with the colour their markers use. */
    const eventGroups = useMemo(() => {
        const groups = new Map<string, string>();
        (events ?? []).forEach((e) => {
            if (e.group && !groups.has(e.group)) groups.set(e.group, e.color ?? EVENT_PALETTE[groups.size % EVENT_PALETTE.length]);
        });
        return Array.from(groups.entries());
    }, [events]);
    const shownEvents = useMemo(() => {
        if (!events) return events;
        const colors = new Map(eventGroups);
        return events.filter((e) => !e.group || !hiddenGroups.has(e.group)).map((e) => (e.color || !e.group ? e : { ...e, color: colors.get(e.group) }));
    }, [events, eventGroups, hiddenGroups]);

    useEffect(() => {
        if (!chart) return;
        const main = series[0] && seriesRef.current.get(series[0].id);
        if (!main) return;
        const events = shownEvents;
        if (!events || !events.length) {
            if (eventsRef.current) {
                chart.removePrimitive(eventsRef.current.id);
                eventsRef.current = null;
            }
            return;
        }
        if (!eventsRef.current) eventsRef.current = addEvents(chart, main, events);
        else eventsRef.current.setEvents(events);
    }, [chart, shownEvents, series]);

    // Range.
    const applyRange = useCallback((btn: RangeButton | null) => {
        const c = chartRef.current;
        if (!c) return;
        const times = c.times();
        if (!times.length) return;
        const last = times[times.length - 1];
        if (!btn || (btn.days === 0 && btn.kind !== "ytd")) {
            c.timeScale.fitContent();
            setActiveRange(btn?.label ?? null);
            return;
        }
        const from = btn.kind === "ytd" ? dayjs.utc(last).startOf("year").valueOf() : last - btn.days * 86_400_000;
        c.timeScale.setVisibleTimeRange(Math.max(times[0], from), last);
        setActiveRange(btn.label);
    }, []);
    const rangeApplied = useRef(false);
    useEffect(() => {
        if (!chart || !hasData || rangeApplied.current || !initialRange) return;
        const buttons = rangeButtons || DEFAULT_RANGE_BUTTONS;
        const btn = typeof initialRange === "string" ? (buttons.find((b) => b.label === initialRange) ?? null) : { label: `${initialRange}d`, days: initialRange };
        rangeApplied.current = true;
        applyRange(btn);
    }, [chart, hasData, initialRange, rangeButtons, applyRange]);

    const toggleHidden = useCallback(
        (id: string) => {
            const next = new Set(hiddenRef.current);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            setHidden(next);
        },
        [setHidden]
    );
    const soloSeries = useCallback(
        (id: string) => {
            setHidden(new Set(latest.current.series.filter((s) => s.id !== id && s.legend !== false).map((s) => s.id)));
        },
        [setHidden]
    );
    const showAll = useCallback(() => setHidden(new Set()), [setHidden]);

    // Crosshair → tooltip and callbacks.
    useEffect(() => {
        if (!chart) return;
        const values = (e: CrosshairEvent): Record<string, number> => {
            const out: Record<string, number> = {};
            for (const s of latest.current.series) {
                const model = seriesRef.current.get(s.id);
                const v = model && e.seriesValues.get(model.id);
                if (!v) continue;
                out[s.id] = "value" in v ? v.value : v.close;
            }
            return out;
        };
        const offMove = chart.subscribeCrosshairMove((e) => {
            const p = latest.current;
            p.onCrosshair?.({ time: e.time, values: values(e) });
            // boxRef carries `tooltip` with its default applied; `p` is the raw props,
            // where an unset `tooltip` is undefined, not true.
            if (!boxRef.current || e.time === null || !e.point) {
                setTip(null);
                return;
            }
            const rows: Array<{ label: string; value: string; color?: string; nearest?: boolean }> = [];
            // Which line the pointer is on. With sixteen registers on one
            // chart the box is a list, and the question the reader has is
            // "what is this line" - so the nearest one to the cursor is
            // named first and set in bold, measured on the pane the engine
            // draws it on.
            let nearest: { index: number; distance: number } | null = null;
            p.series.forEach((s, i) => {
                if (s.visible === false || s.tooltip === false) return;
                const model = seriesRef.current.get(s.id);
                const v = model && e.seriesValues.get(model.id);
                if (!v) return;
                const fmt = valueFormatter(s);
                const color = s.color ?? theme.palette[i % theme.palette.length];
                const scalar = "value" in v ? v.value : v.close;
                const y = model && e.point ? chart.seriesCoordinate(model.id, scalar) : null;
                if (y !== null && y !== undefined && e.point) {
                    const distance = Math.abs(y - e.point.y);
                    if (!nearest || distance < nearest.distance) nearest = { index: rows.length, distance };
                }
                if ("value" in v) rows.push({ label: s.title, value: fmt(v.value), color });
                else rows.push({ label: s.title, value: `O ${fmt(v.open)} H ${fmt(v.high)} L ${fmt(v.low)} C ${fmt(v.close)}`, color });
            });
            if (nearest !== null && rows.length > 1 && (nearest as { index: number; distance: number }).distance < 40) {
                const { index } = nearest as { index: number; distance: number };
                const [hit] = rows.splice(index, 1);
                rows.unshift({ ...hit, nearest: true });
            }
            if (p.tooltipExtra) rows.push(...p.tooltipExtra(e.time));
            if (!rows.length) {
                setTip(null);
                return;
            }
            const title = p.tooltipTitle ? p.tooltipTitle(e.time) : p.resolution && /^\d+$/.test(p.resolution) ? `${dayjs.utc(e.time).tz(p.zone).format("ddd D MMM YYYY HH:mm")} ${zoneLabel(p.zone, e.time)}` : formatDate(dayjs.utc(e.time));
            setTip({ x: e.point.x, y: e.point.y, title, rows });
        });
        const offClick = chart.subscribeClick((e) => {
            const p = latest.current;
            p.onClick?.({ time: e.time, values: values(e), event: e });
            if (!p.clickToHide || !e.point) return;
            // The line under the pointer, measured the way the tooltip picks
            // its nearest row. A click that lands on nothing does nothing.
            const point = e.point;
            const distances = p.series
                .filter((s) => s.visible !== false && !hiddenRef.current.has(s.id))
                .map((s) => {
                    const model = seriesRef.current.get(s.id);
                    const v = model && e.seriesValues.get(model.id);
                    if (!model || !v) return { id: s.id, distance: Number.POSITIVE_INFINITY };
                    const y = chart.seriesCoordinate(model.id, "value" in v ? v.value : v.close);
                    return { id: s.id, distance: y === null || y === undefined ? Number.POSITIVE_INFINITY : Math.abs(y - point.y) };
                });
            const hit = nearestWithin(distances);
            if (hit) toggleHidden(hit);
        });
        // The legend's caret. The engine only tells us it was clicked and where;
        // the menu is ours.
        const offLegend = chart.subscribeLegendMenu((e) => {
            const entry = Array.from(seriesRef.current.entries()).find(([, model]) => model.id === e.seriesId);
            if (entry) setLegendMenu({ id: entry[0], anchor: e.anchor });
        });
        return () => {
            offMove();
            offClick();
            offLegend();
        };
    }, [chart, theme, toggleHidden]);

    // The legend rows themselves. The engine builds them as DOM and rebuilds
    // them on every crosshair move, so a listener on a row would be gone a
    // frame later; the container listens instead and works out which row was
    // hit. A row names no series of its own, but its "⋯" button does.
    useEffect(() => {
        const el = containerRef.current;
        if (!chart || !el) return;
        const onLegendClick = (event: MouseEvent) => {
            const p = latest.current;
            if (p.legendToggles === false) return;
            const target = event.target as HTMLElement | null;
            if (!target || target.closest(".oc-legend-menu, .oc-legend-toggle, .oc-legend-collapse")) return;
            const row = target.closest<HTMLElement>(".oc-legend-row");
            if (!row) return;
            const engineId = row.querySelector<HTMLElement>(".oc-legend-menu")?.dataset.seriesId;
            const entry = engineId && Array.from(seriesRef.current.entries()).find(([, model]) => model.id === engineId);
            if (!entry) return;
            event.preventDefault();
            event.stopPropagation();
            if (event.shiftKey) soloSeries(entry[0]);
            else toggleHidden(entry[0]);
        };
        el.addEventListener("click", onLegendClick, true);
        return () => el.removeEventListener("click", onLegendClick, true);
    }, [chart, soloSeries, toggleHidden]);

    useImperativeHandle(
        ref,
        () => ({
            get chart() {
                return chartRef.current;
            },
            get container() {
                return containerRef.current;
            },
            setRange: (from, to) => chartRef.current?.timeScale.setVisibleTimeRange(from, to),
            fit: () => chartRef.current?.timeScale.fitContent(),
            exportPNG: (name) => (containerRef.current ? exportChartPNG(containerRef.current, `${name ?? exportName}.png`, theme.background) : Promise.resolve()),
            exportCSV: (name) => exportRowsCSV(seriesCSVRows(latest.current.series, /^\d+$/.test(latest.current.resolution ?? "1D")), `${name ?? exportName}.csv`),
        }),
        [exportName, theme.background]
    );

    const buttons = rangeButtons === false ? null : (rangeButtons ?? DEFAULT_RANGE_BUTTONS);
    const showExport = exportMenu ?? Boolean(buttons);
    const tipStyle = useMemo<CSSProperties | undefined>(() => {
        if (!tip || !containerRef.current) return undefined;
        const w = containerRef.current.clientWidth;
        const left = tip.x + 14 + 220 > w ? Math.max(0, tip.x - 14 - 220) : tip.x + 14;
        return { left, top: Math.max(0, tip.y - 12) };
    }, [tip]);

    return (
        <div data-ortex-chart="" className={[styles.root, className ?? ""].join(" ")} style={{ height: height ?? "100%", ...style }}>
            {(buttons || toolbar || showExport) && (
                <div className={styles.bar}>
                    <div className={styles.ranges}>
                        {buttons?.map((b) => (
                            <button key={b.label} type="button" className={[styles.rangeButton, activeRange === b.label ? styles.active : ""].join(" ")} onClick={() => applyRange(b)}>
                                {b.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tools}>
                        {toolbar}
                        {showExport && (
                            <div className={styles.menuWrap}>
                                <button type="button" className={styles.iconButton} aria-label="Export" title="Export" onClick={() => setMenuOpen((o) => !o)}>
                                    ⋯
                                </button>
                                {menuOpen && (
                                    <div className={styles.menu} onMouseLeave={() => setMenuOpen(false)}>
                                        <button type="button" onClick={() => void (setMenuOpen(false), containerRef.current && exportChartPNG(containerRef.current, `${exportName}.png`, surfaceOf(theme)))}>
                                            Download PNG
                                        </button>
                                        <button type="button" onClick={() => void (setMenuOpen(false), exportRowsCSV(seriesCSVRows(series, intraday), `${exportName}.csv`))}>
                                            Download CSV
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
            <Menu
                open={legendMenu !== null}
                anchorEl={legendMenu?.anchor ?? null}
                onClose={() => setLegendMenu(null)}
                slotProps={{
                    list: { dense: true },
                }}
            >
                <MenuItem
                    onClick={() => {
                        if (legendMenu) toggleHidden(legendMenu.id);
                        setLegendMenu(null);
                    }}
                >
                    {legendMenu && hidden.has(legendMenu.id) ? "Show" : "Hide"}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (legendMenu) soloSeries(legendMenu.id);
                        setLegendMenu(null);
                    }}
                >
                    Only this
                </MenuItem>
                <MenuItem
                    disabled={hidden.size === 0}
                    onClick={() => {
                        showAll();
                        setLegendMenu(null);
                    }}
                >
                    Show all
                </MenuItem>
            </Menu>
            {eventLegend && eventGroups.length > 0 && (
                <div className={styles.eventLegend}>
                    {eventGroups.map(([group, color]) => (
                        <button
                            key={group}
                            type="button"
                            className={[styles.eventGroup, hiddenGroups.has(group) ? styles.off : ""].join(" ")}
                            onClick={() =>
                                setHiddenGroups((h) => {
                                    const next = new Set(h);
                                    if (next.has(group)) next.delete(group);
                                    else next.add(group);
                                    return next;
                                })
                            }
                        >
                            <span className={styles.swatch} style={{ background: color }} />
                            {group}
                        </button>
                    ))}
                </div>
            )}
            <div className={[styles.canvasWrap, obfuscated ? styles.obfuscated : ""].join(" ")}>
                <div ref={containerRef} className={styles.canvas} />
                {!hasData && empty !== undefined && <div className={styles.empty}>{empty}</div>}
                {tip && tipStyle && !obfuscated && (
                    <div className={styles.tooltip} style={tipStyle}>
                        <div className={styles.tooltipTitle}>{tip.title}</div>
                        {tip.rows.map((r, i) => (
                            <div key={`${r.label}-${i}`} className={styles.tooltipRow} style={r.nearest ? { fontWeight: 700 } : undefined}>
                                {r.color && <span className={styles.swatch} style={{ background: r.color }} />}
                                <span className={styles.tooltipLabel}>{r.nearest ? `▸ ${r.label}` : r.label}</span>
                                <span className={styles.tooltipValue}>{r.value}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});

export default TimeSeriesChart;
